import React, { useState, useEffect, useRef } from 'react';
import { FaBook, FaPlus, FaUpload, FaDownload, FaEdit, FaTrash, FaTimes, FaSave } from 'react-icons/fa';
import { addBook, getAllBooks, importBooksBatch, updateBook, deleteBook, getAllCategories } from '../../supabase/database';
import Papa from 'papaparse'; // Library untuk parsing CSV
import * as XLSX from 'xlsx'; // Library untuk parsing Excel
import { getCategoryDiscountQuota } from '../../utils/categoryQuota';
import Modal from '../../components/ui/Modal';

const DatabaseBuku = () => {
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ kode_buku: '', judul_buku: '', harga_buku: '', kategori: '' });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [editForm, setEditForm] = useState({ kode_buku: '', judul_buku: '', harga_buku: '', kategori: '' });
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0); // Untuk force re-render
  const fileInputRef = useRef(null);

  const fetchBooks = async () => {
    console.log('🔍 fetchBooks started...');
    setLoading(true);
    try {
      const bookList = await getAllBooks();
      console.log('📚 fetchBooks got data:', bookList.length, 'books');
      console.log('🔍 First few books:', bookList.slice(0, 3).map(b => ({ id: b.id, kode_buku: b.kode_buku, kategori: b.kategori })));
      
      // Force update dengan callback untuk memastikan state ter-update
      setBooks([...bookList]); // Spread to create new array reference
      console.log('✅ fetchBooks completed, state updated');
    } catch (error) {
      console.error('❌ fetchBooks error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    const categoryList = await getAllCategories();
    setCategories(categoryList);
  };

  useEffect(() => {
    fetchBooks();
    fetchCategories();
  }, []);

  useEffect(() => {
    setShowBulkActions(selectedBooks.length > 0);
  }, [selectedBooks]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddBook = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      await addBook({
        ...formData,
        harga_buku: Number(formData.harga_buku)
      });
      setMessage({ type: 'success', text: `Buku "${formData.judul_buku}" berhasil ditambahkan.` });
      setFormData({ kode_buku: '', judul_buku: '', harga_buku: '', kategori: '' });
      fetchBooks(); // Refresh daftar buku
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal menambahkan buku. Kode buku mungkin sudah ada.' });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditBook = (book) => {
    setEditingBook(book.id);
    setEditForm({
      kode_buku: book.kode_buku,
      judul_buku: book.judul_buku,
      harga_buku: book.harga_buku.toString(),
      kategori: book.kategori || ''
    });
  };

  const handleSaveEdit = async () => {
    setIsSubmitting(true);
    try {
      await updateBook(editingBook, {
        ...editForm,
        harga_buku: Number(editForm.harga_buku)
      });
      setMessage({ type: 'success', text: 'Buku berhasil diperbarui.' });
      setEditingBook(null);
      setEditForm({ kode_buku: '', judul_buku: '', harga_buku: '', kategori: '' });
      fetchBooks();
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal memperbarui buku.' });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingBook(null);
    setEditForm({ kode_buku: '', judul_buku: '', harga_buku: '', kategori: '' });
  };

  const handleDeleteBook = async (bookId, bookTitle) => {
    if (window.confirm(`Apakah Anda yakin ingin menghapus buku "${bookTitle}"?`)) {
      try {
        await deleteBook(bookId);
        setMessage({ type: 'success', text: 'Buku berhasil dihapus.' });
        fetchBooks();
      } catch (error) {
        setMessage({ type: 'error', text: 'Gagal menghapus buku.' });
        console.error(error);
      }
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();

    try {
      let booksToImport = [];

      if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        // Handle Excel file
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet);

            booksToImport = jsonData.map(row => ({
              kode_buku: row['Kode Buku'] || row.kode_buku,
              judul_buku: row['Judul Buku'] || row.judul_buku,
              harga_buku: Number(row['Harga Buku'] || row.harga_buku),
              kategori: row['Kategori'] || row.kategori || ''
            })).filter(book => book.kode_buku && book.judul_buku && !isNaN(book.harga_buku));

            if (booksToImport.length > 0) {
              await importBooksBatch(booksToImport);
              setMessage({ type: 'success', text: `${booksToImport.length} buku berhasil diimpor dari Excel.` });
              fetchBooks();
            } else {
              setMessage({ type: 'error', text: 'Format Excel tidak valid atau tidak ada data untuk diimpor.' });
            }
          } catch (error) {
            setMessage({ type: 'error', text: 'Gagal mengimpor data Excel.' });
            console.error(error);
          }
        };
        reader.readAsArrayBuffer(file);
      } else if (fileExtension === 'csv') {
        // Handle CSV file
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: async (results) => {
            booksToImport = results.data.map(row => ({
              kode_buku: row['Kode Buku'] || row.kode_buku,
              judul_buku: row['Judul Buku'] || row.judul_buku,
              harga_buku: Number(row['Harga Buku'] || row.harga_buku),
              kategori: row['Kategori'] || row.kategori || ''
            })).filter(book => book.kode_buku && book.judul_buku && !isNaN(book.harga_buku));

            if (booksToImport.length > 0) {
              try {
                await importBooksBatch(booksToImport);
                setMessage({ type: 'success', text: `${booksToImport.length} buku berhasil diimpor dari CSV.` });
                fetchBooks();
              } catch (error) {
                setMessage({ type: 'error', text: 'Gagal mengimpor data CSV.' });
                console.error(error);
              }
            } else {
              setMessage({ type: 'error', text: 'Format CSV tidak valid atau tidak ada data untuk diimpor.' });
            }
          }
        });
      } else {
        setMessage({ type: 'error', text: 'Format file tidak didukung. Gunakan file Excel (.xlsx) atau CSV (.csv).' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Terjadi kesalahan saat membaca file.' });
      console.error(error);
    }

    // Reset file input
    e.target.value = null;
  };

  // Bulk selection functions
  const handleSelectAll = (checked) => {
    if (checked) {
      // Pastikan books adalah array dan ambil semua ID yang valid
      const allBookIds = Array.isArray(books) ? books.map(book => book.id).filter(id => id) : [];
      setSelectedBooks(allBookIds);
    } else {
      setSelectedBooks([]);
    }
  };

  const handleSelectBook = (bookId, checked) => {
    if (!bookId) return; // Pastikan bookId valid
    
    setSelectedBooks(prev => {
      // Pastikan prev adalah array
      const currentSelection = Array.isArray(prev) ? prev : [];
      
      if (checked) {
        // Tambah hanya jika belum ada
        if (!currentSelection.includes(bookId)) {
          return [...currentSelection, bookId];
        }
        return currentSelection;
      } else {
        // Hapus dari selection
        return currentSelection.filter(id => id !== bookId);
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedBooks.length === 0) return;
    
    if (window.confirm(`Apakah Anda yakin ingin menghapus ${selectedBooks.length} buku yang dipilih?`)) {
      try {
        setLoading(true);
        
        // Delete setiap buku secara individual dengan error handling yang lebih baik
        for (const bookId of selectedBooks) {
          await deleteBook(bookId);
        }
        
        setMessage({ type: 'success', text: `${selectedBooks.length} buku berhasil dihapus.` });
        setSelectedBooks([]);
        fetchBooks();
      } catch (error) {
        setMessage({ type: 'error', text: `Gagal menghapus buku: ${error.message}` });
        console.error('Error deleting books:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkUpdateCategory = async (newCategory) => {
    console.log('Selected books:', selectedBooks, 'New category:', newCategory); // Debug log
    
    if (!selectedBooks || selectedBooks.length === 0 || !newCategory) {
      setMessage({ type: 'error', text: 'Tidak ada buku yang dipilih atau kategori tidak valid.' });
      return;
    }
    
    if (window.confirm(`Apakah Anda yakin ingin mengubah kategori ${selectedBooks.length} buku yang dipilih menjadi "${newCategory}"?`)) {
      try {
        setLoading(true);
        
        // Pastikan selectedBooks adalah array
        const bookIds = Array.isArray(selectedBooks) ? selectedBooks : [];
        
        if (bookIds.length === 0) {
          throw new Error('Tidak ada buku yang dipilih untuk diupdate');
        }
        
        // Update setiap buku secara individual
        for (const bookId of bookIds) {
          if (bookId) { // Pastikan bookId tidak undefined
            await updateBook(bookId, { kategori: newCategory });
          }
        }
        
        setMessage({ type: 'success', text: `Kategori ${bookIds.length} buku berhasil diubah.` });
        setSelectedBooks([]);
        setBulkAction('');
        
        // Force refresh dengan logging
        console.log('🔄 Refreshing books after bulk category update...');
        await fetchBooks();
        console.log('✅ Books refreshed, new count:', books.length);
        
        // Force re-render dengan mengubah refresh counter
        setRefreshCounter(prev => prev + 1);
        console.log('🔄 Forcing re-render with refresh counter');
        
        // Clear message setelah 3 detik
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      } catch (error) {
        setMessage({ type: 'error', text: `Gagal mengubah kategori buku: ${error.message}` });
        console.error('Error updating book category:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="p-4 md:p-8 bg-white min-h-full">
      <div className="flex items-center gap-4 mb-6">
        <FaBook className="text-4xl text-amber-500" />
        <h1 className="text-3xl font-bold text-gray-800">Database Buku</h1>
      </div>

      {/* Form Tambah & Impor */}
      <div className="bg-gray-50 p-6 rounded-lg shadow-sm mb-8">
        <h2 className="text-xl font-bold text-gray-700 mb-4">Tambah Buku Baru</h2>
        <form onSubmit={handleAddBook} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input name="kode_buku" value={formData.kode_buku} onChange={handleChange} placeholder="Kode Buku" required className="p-2 border rounded-md" />
            <input name="judul_buku" value={formData.judul_buku} onChange={handleChange} placeholder="Judul Buku" required className="p-2 border rounded-md" />
            <input name="harga_buku" type="number" value={formData.harga_buku} onChange={handleChange} placeholder="Harga Buku" required className="p-2 border rounded-md" />
            <select name="kategori" value={formData.kategori} onChange={handleChange} className="p-2 border rounded-md">
              <option value="">Pilih Kategori</option>
              {categories.map(category => (
                <option key={category.id} value={category.nama_kategori}>
                  {category.nama_kategori}
                </option>
              ))}
            </select>
          </div>
          {formData.kategori && (
            <div className="text-xs text-gray-500 -mt-2">
              Kuota Diskon kategori ini: {(() => {
                const cat = categories.find(c => c.nama_kategori === formData.kategori);
                return (cat?.kuota_diskon ?? getCategoryDiscountQuota(formData.kategori) ?? 0);
              })()}%
            </div>
          )}
          {message.text && (
            <p className={`text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{message.text}</p>
          )}
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload} className="hidden" ref={fileInputRef} />
              <a
                href="/template_daftar_buku.xlsx"
                download
                className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
              >
                <FaDownload /> Download Template Excel
              </a>
              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
              >
                <FaUpload /> Impor Excel/CSV
              </button>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold py-2 px-4 rounded-lg transition-colors disabled:bg-gray-400"
            >
              <FaPlus /> {isSubmitting ? 'Menambahkan...' : 'Tambah Buku'}
            </button>
          </div>
        </form>
      </div>

      {/* Daftar Buku */}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-700">Daftar Buku</h2>
          
          {/* Bulk Actions */}
          {selectedBooks.length > 0 && (
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {selectedBooks.length} buku dipilih
              </span>
              <div className="flex items-center gap-2">
                <select
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Pilih Aksi</option>
                  <option value="delete">Hapus</option>
                  <option value="category">Ubah Kategori</option>
                </select>
                
                {bulkAction === 'category' && (
                  <select
                    onChange={(e) => e.target.value && handleBulkUpdateCategory(e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">Pilih Kategori</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.nama_kategori}>
                        {category.nama_kategori}
                      </option>
                    ))}
                  </select>
                )}
                
                {bulkAction === 'delete' && (
                  <button
                    onClick={handleBulkDelete}
                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                  >
                    Hapus
                  </button>
                )}
                
                <button
                  onClick={() => {
                    setSelectedBooks([]);
                    setBulkAction('');
                  }}
                  className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded transition-colors"
                >
                  Batal
                </button>
              </div>
            </div>
          )}
        </div>
        
        <table key={`books-table-${refreshCounter}`} className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3">
                <input
                  type="checkbox"
                  checked={selectedBooks.length === books.length && books.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kode Buku</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Judul Buku</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kuota Diskon</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Harga</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aksi</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="7" className="text-center p-4">Memuat data...</td></tr>
            ) : (
              books.map(book => (
                <tr key={book.id}>
                  {editingBook === book.id ? (
                    <>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedBooks.includes(book.id)}
                          onChange={(e) => handleSelectBook(book.id, e.target.checked)}
                          className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          name="kode_buku"
                          value={editForm.kode_buku}
                          onChange={handleEditFormChange}
                          className="w-full p-1 border rounded text-sm"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          name="judul_buku"
                          value={editForm.judul_buku}
                          onChange={handleEditFormChange}
                          className="w-full p-1 border rounded text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          name="kategori"
                          value={editForm.kategori}
                          onChange={handleEditFormChange}
                          className="w-full p-1 border rounded text-sm"
                        >
                          <option value="">Pilih Kategori</option>
                          {categories.map(category => (
                            <option key={category.id} value={category.nama_kategori}>
                              {category.nama_kategori}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          const cat = categories.find(c => c.nama_kategori === editForm.kategori);
                          return (cat?.kuota_diskon ?? getCategoryDiscountQuota(editForm.kategori) ?? 0);
                        })()}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          name="harga_buku"
                          type="number"
                          value={editForm.harga_buku}
                          onChange={handleEditFormChange}
                          className="w-full p-1 border rounded text-sm"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={handleSaveEdit}
                            disabled={isSubmitting}
                            className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-all duration-200"
                            title="Simpan"
                          >
                            <FaSave />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition-all duration-200"
                            title="Batal"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedBooks.includes(book.id)}
                          onChange={(e) => handleSelectBook(book.id, e.target.checked)}
                          className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">{book.kode_buku}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{book.judul_buku}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{book.kategori || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{(() => {
                        const cat = categories.find(c => c.nama_kategori === book.kategori);
                        return (cat?.kuota_diskon ?? getCategoryDiscountQuota(book.kategori) ?? 0);
                      })()}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        Rp {Number(book.harga_buku).toLocaleString('id-ID')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEditBook(book)}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-all duration-200"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDeleteBook(book.id, book.judul_buku)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-all duration-200"
                            title="Hapus"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
            {!loading && books.length === 0 && (
              <tr><td colSpan="7" className="text-center p-4 text-gray-500">Belum ada buku di database.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DatabaseBuku;

